import { createDateFormatter, type TimezoneMode } from "@nexus/ui";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useRestClient, type ApiFetch } from "@/lib/restClient";
import type {
  ConfigurationManifest,
  ConfigurationSelection,
  ExportResult,
  ImportResult,
  Resolutions,
} from "./configurationModel";

export type TransferOperation = "export" | "import";
export interface TransferSummary {
  id: string;
  operation: TransferOperation;
  format: string;
  status: "pending" | "running" | "succeeded" | "failed";
  dry_run: boolean;
  requested_by: string | null;
  created_at: string;
  finished_at: string | null;
  retry_of: string | null;
}
export interface TransferDetail extends TransferSummary {
  request: {
    selection?: ConfigurationSelection;
    configuration?: ConfigurationManifest;
    resolutions?: Resolutions | null;
  };
  result: {
    configuration?: ConfigurationManifest;
    resources?: (ExportResult["resources"][number] | ImportResult["resources"][number])[];
    applied?: boolean;
    valid?: boolean;
  } | null;
  errors: string[];
}
interface TransferPage {
  items: TransferSummary[];
  total: number;
  offset: number;
  limit: number;
}

export function useTransferHistory(operation: TransferOperation, offset: number, search: string) {
  const api = useRestClient();
  return useQuery({
    queryKey: ["data-transfers", operation, offset, search],
    queryFn: () =>
      api<TransferPage>(
        `/api/admin/data-transfers?${new URLSearchParams({ operation, offset: String(offset), search })}`,
      ),
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      query.state.data?.items.some((row) => isTransferActive(row.status)) ? 2500 : false,
  });
}

export function useTransferRun(id?: string) {
  const api = useRestClient();
  return useQuery({
    queryKey: ["data-transfer", id],
    queryFn: () => api<TransferDetail>(`/api/admin/data-transfers/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data && isTransferActive(query.state.data.status) ? 2500 : false,
  });
}

export interface AcceptedTransfer {
  run_id: string;
  status: "pending";
}

export function isTransferActive(status: TransferSummary["status"]) {
  return status === "pending" || status === "running";
}

export function useTransferDispatch(id?: string) {
  const api = useRestClient();
  const client = useQueryClient();
  const error = useQuery<string | null>({
    queryKey: ["transfer-dispatch", id],
    queryFn: async () => null,
    enabled: false,
  });
  return {
    error: error.data,
    execute: () => {
      if (id) dispatchTransfer(api, client, id);
    },
  };
}

export function dispatchTransfer(api: ApiFetch, client: QueryClient, id: string) {
  client.setQueryData(["transfer-dispatch", id], null);
  void api(`/api/admin/data-transfers/${encodeURIComponent(id)}/execute`, {
    method: "POST",
    body: "{}",
    keepalive: true,
  })
    .catch(() => {
      client.setQueryData(
        ["transfer-dispatch", id],
        "Execution could not be confirmed. Check the current status before retrying.",
      );
    })
    .finally(() => {
      for (const key of [
        "data-transfers",
        "data-transfer",
        "tenants",
        "widgets-admin",
        "widget-catalog",
        "dashboards",
      ])
        void client.invalidateQueries({ queryKey: [key] });
    });
}

export function useStartExport() {
  const api = useRestClient();
  const client = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (retryOf?: string) =>
      api<AcceptedTransfer>("/api/admin/data-transfers/export", {
        method: "POST",
        body: JSON.stringify({ format: "json", retry_of: retryOf }),
      }),
    onSuccess: (result) => {
      dispatchTransfer(api, client, result.run_id);
      navigate(`/admin/data-transfer/${result.run_id}`, { state: { started: true } });
    },
  });
}

export function transferDate(value: string, timezone: TimezoneMode = "utc"): string {
  return createDateFormatter(timezone, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
}

export function downloadTransfer(
  configuration: ConfigurationManifest,
  fileName = "nexus-configuration.json",
) {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(configuration) + "\n"], { type: "application/json" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
